import { Component, effect, DestroyRef, inject, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, NgIf } from '@angular/common';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import * as _ from 'lodash';
import { SettingsService } from 'src/app/shared/services/settings';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CanComponentDeactivate } from 'src/app/guards/can-deactivate.guard';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
// Declare Helpers globally
declare const Helpers: any;

@Component({
  selector: 'app-litmus-action-ageing',
  templateUrl: './litmus-action-ageing.component.html',
  styleUrls: ['./litmus-action-ageing.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LtmsLoaderComponent]
})
export class LitmusActionAgeingComponent implements OnInit, OnChanges, OnDestroy, CanComponentDeactivate {
  @Input() readonly: boolean = true;

  destroyRef = inject(DestroyRef);
  ageingForm!: FormGroup;
  businessDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  isSavingSection: 'calculations' | 'calendar' | null = null;
  isCancellingSection: 'calculations' | 'calendar' | null = null;
  isCalculationsSaveEnabled = false;
  isCalendarSaveEnabled = false;
  isSaveEnabled = false;
  originalConfiguration: any = {};
  configuration: any = {};
  brandDetails: any = {};
  configurationLoaded: boolean = false;
  ageingFormConfig: any = {};
  private initializedBrandId: string | null = null;
  
  // Status list properties
  statusList: any[] = [];
  selectedClosedStatuses: string[] = [];
  showStatusDropdown = false; // Set to true for debugging
  holidayDates: string[] = [];
  originalHolidayDates: string[] = [];
  holidayPickerValue = '';

  constructor(
    private fb: FormBuilder,
    private _toast: ToastService,
    private _settingsService: SettingsService,
    private stateService: AppStateService,
    private accountsService: AccountsService
  ) {
    effect(() => {
      if (this.stateService.initialized()) {
        const brandDetail: any = this.stateService.brandDetails();
        if (_.isEmpty(brandDetail)) {
          return;
        }
        if (this.ageingForm && this.initializedBrandId === brandDetail._id) {
          this.brandDetails = brandDetail;
          this.applyBrandDetailsToForm();
          return;
        }
        this.configurationLoaded = false;
        this.brandDetails = brandDetail;
        this.initializedBrandId = brandDetail._id;
        this.initializeForm();
      }
    })
  }

  ngOnInit(): void {
    this.configurationLoaded = false;
    this.originalConfiguration = { ...this.configuration };
    // this.initializeForm();

    // Add document click listener to close dropdown
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    // Remove document click listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly']) {
      this.updateFormReadonlyState();
    }
  }

  private updateFormReadonlyState(): void {
    if (!this.ageingForm) {
      return;
    }
    if (this.readonly) {
      this.ageingForm.disable({ emitEvent: false });
      this.showStatusDropdown = false;
    } else {
      this.ageingForm.enable({ emitEvent: false });
    }
  }

  private initializeForm(): void {
    // Initialize status list from brand details
    this.statusList = this.brandDetails?.status_list || [];
    
    // Initialize selected closed statuses - convert UUIDs to names for display
    const selectedStatusUuids = this.brandDetails?.lw_status_list_map?.LW_CLOSED || [];
    this.selectedClosedStatuses = selectedStatusUuids.map(uuid => {
      const status = this.statusList.find(s => s.uuid === uuid);
      return status ? status.name : uuid; // Use name if found, fallback to UUID
    });

    // Initialize form with actual brand details values
    const conversationConfig = this.brandDetails?.conversation_config?.actions || {};
    const businessTimings = conversationConfig.business_timings || {};
    const workTiming = businessTimings.worktiming || {};
    const startTime = workTiming.start || {};
    const endTime = workTiming.end || {};

    // Convert time format from h/m to HH:MM
    const startTimeFormatted = startTime.h !== undefined && startTime.m !== undefined
      ? `${String(startTime.h).padStart(2, '0')}:${String(startTime.m).padStart(2, '0')}`
      : '09:00';
    const endTimeFormatted = endTime.h !== undefined && endTime.m !== undefined
      ? `${String(endTime.h).padStart(2, '0')}:${String(endTime.m).padStart(2, '0')}`
      : '18:00';

    // Get business days from weekends
    const weekends = businessTimings.weekends || [];
    const selectedBusinessDays = this.businessDays.filter((day, index) => !weekends.includes(index + 1));

    this.ageingForm = this.fb.group({
      closedStatus: [this.selectedClosedStatuses], // Changed to array for multiple selection
      ruleEngineFrequency: [conversationConfig.next_trigger_time_in_minutes || 60],
      maxResponseAge: [conversationConfig.process_interval_time || 15],
      businessStartTime: [startTimeFormatted],
      businessEndTime: [endTimeFormatted],
      selectedBusinessDays: [selectedBusinessDays.length > 0 ? selectedBusinessDays : this.businessDays],
      dontRunNonBusinessHours: [conversationConfig.should_not_run_in_business_hours || false]
    });

    // Listen to form changes with debounce and cleanup
    this.ageingForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        this.onFormChange(value);
      });
    this.loadHolidayDatesFromBrand();
    this.updateFormReadonlyState();
    this.updateSectionSaveState();
    this.configurationLoaded = true;
  }

  private applyBrandDetailsToForm(): void {
    if (!this.ageingForm) {
      return;
    }
    this.statusList = this.brandDetails?.status_list || [];
    if (this.isSaveEnabled) {
      this.updateSectionSaveState();
      return;
    }
    const saved = this.getSavedSnapshot();
    this.selectedClosedStatuses = [...saved.closedStatusNames];
    this.ageingForm.patchValue({
      closedStatus: this.selectedClosedStatuses,
      ruleEngineFrequency: saved.ruleEngineFrequency,
      maxResponseAge: saved.maxResponseAge,
      businessStartTime: saved.businessStartTime,
      businessEndTime: saved.businessEndTime,
      selectedBusinessDays: saved.selectedBusinessDays,
      dontRunNonBusinessHours: saved.dontRunNonBusinessHours
    }, { emitEvent: false });
    this.loadHolidayDatesFromBrand();
    this.updateSectionSaveState();
  }

  onFormChange(_value: any): void {
    this.updateSectionSaveState();
  }

  onBusinessDayToggle(day: string): void {
    if (this.readonly) {
      return;
    }
    const currentDays = this.ageingForm.get('selectedBusinessDays')?.value || [];
    let updatedDays: string[];

    if (currentDays.includes(day)) {
      updatedDays = currentDays.filter((d: string) => d !== day);
    } else {
      updatedDays = [...currentDays, day];
    }

    this.ageingForm.patchValue({ selectedBusinessDays: updatedDays });
    this.updateSectionSaveState();
  }

  isBusinessDaySelected(day: string): boolean {
    const selectedDays = this.ageingForm.get('selectedBusinessDays')?.value || [];
    return selectedDays.includes(day);
  }

  private loadHolidayDatesFromBrand(): void {
    const holidays = _.get(this.brandDetails, 'conversation_config.actions.business_timings.holidays', []) || [];
    this.holidayDates = this.normalizeHolidayList(holidays);
    this.originalHolidayDates = [...this.holidayDates];
    this.holidayPickerValue = '';
  }

  private normalizeHolidayList(holidays: any[]): string[] {
    const uniqueDates = new Set<string>();
    (holidays || []).forEach((holiday: any) => {
      const isoDate = this.toHolidayIso(holiday);
      if (isoDate) {
        uniqueDates.add(isoDate);
      }
    });
    return Array.from(uniqueDates).sort();
  }

  private toHolidayIso(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }

    const parsed = value instanceof Date ? value : new Date(value);
    if (isNaN(parsed.getTime())) {
      return null;
    }

    return parsed.toISOString().substring(0, 10);
  }

  formatHolidayLabel(isoDate: string): string {
    const [year, month, day] = (isoDate || '').split('-').map(Number);
    if (!year || !month || !day) {
      return isoDate;
    }

    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  }

  addHolidayDate(): void {
    if (this.readonly) {
      return;
    }

    const isoDate = (this.holidayPickerValue || '').trim();
    if (!isoDate) {
      return;
    }

    if (this.holidayDates.includes(isoDate)) {
      Helpers.smallAlertBox('Holiday already added', 'This date is already in the holiday list.', 'info', 2000);
      this.holidayPickerValue = '';
      return;
    }

    this.holidayDates = [...this.holidayDates, isoDate].sort();
    this.holidayPickerValue = '';
    this.updateSectionSaveState();
  }

  removeHolidayDate(isoDate: string): void {
    if (this.readonly) {
      return;
    }

    this.holidayDates = this.holidayDates.filter((date) => date !== isoDate);
    this.updateSectionSaveState();
  }

  clearHolidayDates(): void {
    if (this.readonly || this.holidayDates.length === 0) {
      return;
    }

    this.holidayDates = [];
    this.holidayPickerValue = '';
    this.updateSectionSaveState();
  }

  private buildHolidayDatesForSave(): Date[] {
    return this.holidayDates.map((isoDate) => new Date(`${isoDate}T00:00:00.000Z`));
  }

  private haveHolidayDatesChanged(): boolean {
    return JSON.stringify(this.holidayDates) !== JSON.stringify(this.originalHolidayDates);
  }

  onInputChange(_field: string, _value: any): void {
    this.updateSectionSaveState();
  }

  // Status dropdown methods
  toggleStatusDropdown(event: Event): void {
    if (this.readonly) {
      return;
    }
    event.stopPropagation();
    this.showStatusDropdown = !this.showStatusDropdown;
  }

  onStatusSelect(status: any): void {
    if (this.readonly) {
      return;
    }
    const statusName = status.name || status;
    if (!this.selectedClosedStatuses.includes(statusName)) {
      this.selectedClosedStatuses.push(statusName);
      this.updateFormValue();
    }
    this.showStatusDropdown = false;
  }

  onStatusRemove(statusName: string): void {
    if (this.readonly) {
      return;
    }
    this.selectedClosedStatuses = this.selectedClosedStatuses.filter(status => status !== statusName);
    this.updateFormValue();
  }

  private updateFormValue(): void {
    this.ageingForm.patchValue({
      closedStatus: this.selectedClosedStatuses
    });
    this.updateSectionSaveState();
  }

  isStatusSelected(status: any): boolean {
    const statusName = status.name || status;
    return this.selectedClosedStatuses.includes(statusName);
  }

  getAvailableStatuses(): any[] {
    // Return all statuses - we'll show them all with selection indicators
    return this.statusList;
  }

  // Handle clicking outside dropdown
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.multi-select-container')) {
      this.showStatusDropdown = false;
    }
  }

  private updateSectionSaveState(): void {
    this.isCalculationsSaveEnabled = this.hasCalculationsChanged();
    this.isCalendarSaveEnabled = this.hasCalendarChanged();
    this.isSaveEnabled = this.isCalculationsSaveEnabled || this.isCalendarSaveEnabled;
  }

  private getSavedSnapshot(): any {
    const conversationConfig = this.brandDetails?.conversation_config?.actions || {};
    const businessTimings = conversationConfig.business_timings || {};
    const workTiming = businessTimings.worktiming || {};
    const startTime = workTiming.start || {};
    const endTime = workTiming.end || {};

    const originalStartTime = startTime.h !== undefined && startTime.m !== undefined
      ? `${String(startTime.h).padStart(2, '0')}:${String(startTime.m).padStart(2, '0')}`
      : '09:00';
    const originalEndTime = endTime.h !== undefined && endTime.m !== undefined
      ? `${String(endTime.h).padStart(2, '0')}:${String(endTime.m).padStart(2, '0')}`
      : '18:00';

    const weekends = businessTimings.weekends || [];
    const originalBusinessDays = this.businessDays.filter((_day, index) => !weekends.includes(index + 1));
    const selectedStatusUuids = this.brandDetails?.lw_status_list_map?.LW_CLOSED || [];
    const closedStatusNames = selectedStatusUuids.map((uuid: string) => {
      const status = this.statusList.find(s => s.uuid === uuid);
      return status ? status.name : uuid;
    });

    return {
      closedStatusNames,
      ruleEngineFrequency: conversationConfig.next_trigger_time_in_minutes || 60,
      maxResponseAge: conversationConfig.process_interval_time || 15,
      businessStartTime: originalStartTime,
      businessEndTime: originalEndTime,
      selectedBusinessDays: originalBusinessDays.length > 0 ? originalBusinessDays : this.businessDays,
      dontRunNonBusinessHours: conversationConfig.should_not_run_in_business_hours || false
    };
  }

  private hasCalculationsChanged(): boolean {
    if (!this.ageingForm || !this.brandDetails) return false;

    const currentValues = this.ageingForm.getRawValue();
    const saved = this.getSavedSnapshot();
    const currentStatuses = [...(this.selectedClosedStatuses || [])].sort();
    const savedStatuses = [...(saved.closedStatusNames || [])].sort();

    return (
      JSON.stringify(currentStatuses) !== JSON.stringify(savedStatuses) ||
      Number(currentValues.ruleEngineFrequency) !== Number(saved.ruleEngineFrequency) ||
      Number(currentValues.maxResponseAge) !== Number(saved.maxResponseAge) ||
      !!currentValues.dontRunNonBusinessHours !== !!saved.dontRunNonBusinessHours
    );
  }

  private hasCalendarChanged(): boolean {
    if (!this.ageingForm || !this.brandDetails) return false;

    const currentValues = this.ageingForm.getRawValue();
    const saved = this.getSavedSnapshot();
    const currentDays = [...(currentValues.selectedBusinessDays || [])].sort();
    const savedDays = [...(saved.selectedBusinessDays || [])].sort();

    return (
      currentValues.businessStartTime !== saved.businessStartTime ||
      currentValues.businessEndTime !== saved.businessEndTime ||
      JSON.stringify(currentDays) !== JSON.stringify(savedDays) ||
      this.haveHolidayDatesChanged()
    );
  }

  private validateCalculationsBeforeSave(formValues: any): boolean {
    if (!formValues.closedStatus || formValues.closedStatus.length === 0) {
      Helpers.smallAlertBox("Error", '"Status to be considered as Closed" is mandatory.', "error");
      return false;
    }

    if (!formValues.ruleEngineFrequency || formValues.ruleEngineFrequency > 60 || formValues.ruleEngineFrequency < 30) {
      Helpers.smallAlertBox("Error", '"Rule engine run frequency" field value should be greater than equal to 30 and lesser than equal to 60 minutes.', "error");
      return false;
    }

    if (!formValues.maxResponseAge || formValues.maxResponseAge <= 0 || formValues.maxResponseAge > 15) {
      Helpers.smallAlertBox("Error", '"Maximum age of response" field value should be greater than 0 and lesser than equal to 15 days.', "error");
      return false;
    }

    return true;
  }

  private validateCalendarBeforeSave(startTime: string[], endTime: string[]): boolean {
    if (startTime[0] && endTime[0] && startTime[1] && endTime[1]) {
      const startHour = parseInt(startTime[0], 10);
      const startMin = parseInt(startTime[1], 10);
      const endHour = parseInt(endTime[0], 10);
      const endMin = parseInt(endTime[1], 10);

      if (startHour > endHour || (startHour === endHour && startMin >= endMin)) {
        Helpers.smallAlertBox("Error", '"Start Time" should be same or less than "End time".', "error");
        return false;
      }
    } else {
      Helpers.smallAlertBox("Error", '"Start Time" and "End Time" should not be blank.', "error");
      return false;
    }

    return true;
  }

  private ensureActionsConfig(): void {
    if (!this.brandDetails.conversation_config) {
      this.brandDetails.conversation_config = { actions: {} };
    }
    if (!this.brandDetails.conversation_config.actions) {
      this.brandDetails.conversation_config.actions = {};
    }
    if (!this.brandDetails.conversation_config.actions.business_timings) {
      this.brandDetails.conversation_config.actions.business_timings = {
        worktiming: { start: {}, end: {} },
        holidays: []
      };
    }
  }

  private applyCalculationsToBrand(formValues: any): void {
    this.ensureActionsConfig();
    this.brandDetails.conversation_config.actions.next_trigger_time_in_minutes = formValues.ruleEngineFrequency || 60;
    this.brandDetails.conversation_config.actions.process_interval_time = formValues.maxResponseAge || 15;
    this.brandDetails.conversation_config.actions.should_not_run_in_business_hours = formValues.dontRunNonBusinessHours || false;
  }

  private applyCalendarToBrand(formValues: any): void {
    this.ensureActionsConfig();
    const startTime = formValues.businessStartTime ? formValues.businessStartTime.split(':') : ['00', '00'];
    const endTime = formValues.businessEndTime ? formValues.businessEndTime.split(':') : ['23', '59'];

    if (startTime[0] && startTime[1]) {
      this.brandDetails.conversation_config.actions.business_timings.worktiming.start.h = parseInt(startTime[0], 10);
      this.brandDetails.conversation_config.actions.business_timings.worktiming.start.m = parseInt(startTime[1], 10);
    } else {
      this.brandDetails.conversation_config.actions.business_timings.worktiming.start.h = 0;
      this.brandDetails.conversation_config.actions.business_timings.worktiming.start.m = 0;
    }

    if (endTime[0] && endTime[1]) {
      this.brandDetails.conversation_config.actions.business_timings.worktiming.end.h = parseInt(endTime[0], 10);
      this.brandDetails.conversation_config.actions.business_timings.worktiming.end.m = parseInt(endTime[1], 10);
    } else {
      this.brandDetails.conversation_config.actions.business_timings.worktiming.end.h = 23;
      this.brandDetails.conversation_config.actions.business_timings.worktiming.end.m = 59;
    }

    const allweekDays = [1, 2, 3, 4, 5, 6, 7];
    const selectedBusinessDays = formValues.selectedBusinessDays || [];
    const weekendDays = allweekDays.filter(day => !selectedBusinessDays.includes(this.businessDays[day - 1]));
    this.brandDetails.conversation_config.actions.business_timings.weekends = weekendDays;
    this.brandDetails.conversation_config.actions.business_timings.holidays = this.buildHolidayDatesForSave();
  }

  private getClosedStatusPayload(formValues: any): any {
    const selectedStatusNames = formValues.closedStatus || [];
    const selectedStatusUuids = selectedStatusNames.map((name: string) => {
      const status = this.statusList.find(s => s.name === name);
      return status ? status.uuid : name;
    });

    return {
      lw_status_list_map: {
        LW_CLOSED: selectedStatusUuids
      }
    };
  }

  saveConfiguration(section: 'calculations' | 'calendar') {
    if (this.readonly) {
      Helpers.smallAlertBox('Access denied', "You do not have write permission", "info", 1500);
      return;
    }
    if (this.isSavingSection) return;

    const formValues = this.ageingForm.getRawValue();
    this.ageingFormConfig = formValues;

    if (section === 'calculations') {
      if (!this.validateCalculationsBeforeSave(formValues)) {
        return;
      }
      this.applyCalculationsToBrand(formValues);
    } else {
      const startTime = formValues.businessStartTime ? formValues.businessStartTime.split(':') : ['00', '00'];
      const endTime = formValues.businessEndTime ? formValues.businessEndTime.split(':') : ['23', '59'];
      if (!this.validateCalendarBeforeSave(startTime, endTime)) {
        return;
      }
      this.applyCalendarToBrand(formValues);
    }

    this.isSavingSection = section;
    this.ensureActionsConfig();

    const updatedConfig = {
      actions: this.brandDetails.conversation_config.actions,
      ...(section === 'calculations'
        ? this.getClosedStatusPayload(formValues)
        : { lw_status_list_map: { LW_CLOSED: this.brandDetails?.lw_status_list_map?.LW_CLOSED || [] } })
    };

    this._settingsService.updateBrandDashboardConfiguration(this.brandDetails._id, updatedConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (res && res.code == 0) {
            this._toast.showPopup("SUCCESS", "Success", "Action ageing configuration has been updated successfully.", 3000);
            if (section === 'calendar') {
              this.originalHolidayDates = [...this.holidayDates];
            }
            this.afterUpdateBrandConfig(section);
          } else if (res && res.data && res.data.error_message) {
            this._toast.showPopup("ERROR", "Error", res.data.error_message, 3000);
          } else {
            this._toast.showPopup("ERROR", "Error", "Error while updating Ageing configurations.", 3000);
          }
          this.isSavingSection = null;
          this.updateSectionSaveState();
        },
        error: (err: any) => {
          console.error('Error saving ageing configurations:', err);
          this._toast.showPopup("ERROR", "ERROR", "Error while saving ageing configurations", 3500);
          this.isSavingSection = null;
        }
      });
  }

  afterUpdateBrandConfig(section: 'calculations' | 'calendar') {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        const unsavedCalendarHolidays = section === 'calculations' ? [...this.holidayDates] : null;
        this.brandDetails = response.data.brand;
        this.stateService.updateBrandDetails(this.brandDetails);
        if (section === 'calendar') {
          this.loadHolidayDatesFromBrand();
        } else if (unsavedCalendarHolidays) {
          this.holidayDates = unsavedCalendarHolidays;
        }
        this.updateSectionSaveState();
      }
    });
  }

  cancelConfiguration(section: 'calculations' | 'calendar') {
    if (this.readonly) {
      Helpers.smallAlertBox('Access denied', "You do not have write permission", "info", 1500);
      return;
    }
    if (this.isCancellingSection) return;

    this.isCancellingSection = section;
    const saved = this.getSavedSnapshot();

    if (section === 'calculations') {
      this.selectedClosedStatuses = [...saved.closedStatusNames];
      this.ageingForm.patchValue({
        closedStatus: this.selectedClosedStatuses,
        ruleEngineFrequency: saved.ruleEngineFrequency,
        maxResponseAge: saved.maxResponseAge,
        dontRunNonBusinessHours: saved.dontRunNonBusinessHours
      });
    } else {
      this.ageingForm.patchValue({
        businessStartTime: saved.businessStartTime,
        businessEndTime: saved.businessEndTime,
        selectedBusinessDays: saved.selectedBusinessDays
      });
      this.loadHolidayDatesFromBrand();
    }

    this.updateSectionSaveState();
    this.isCancellingSection = null;
  }

  /**
   * Can deactivate guard implementation
   */
  canDeactivate(): Promise<boolean> | boolean {
    const hasUnsavedChanges = this.isSaveEnabled;
    if (hasUnsavedChanges) {
      return this.confirmDiscardChanges();
    }
    return true;
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  confirmDiscardChanges(): Promise<boolean> {
    return new Promise((resolve) => {
      if (Helpers && Helpers.SmartMessageBox) {
        Helpers.SmartMessageBox(
          {
            title: "You have reordered the rows",
            content: "To save your changes, please use the <b>Save</b> button at the bottom of the card. Click <b>Discard Changes</b> to lose your edits and continue. Click <b>Cancel</b> to close this popup and keep editing.",
            buttons: [
              { text: "Cancel", type: "secondary", key: "Cancel" },
              { text: "Discard Changes", type: "primary", key: "Discard" }
            ]
          },
          (ButtonPressed: string) => {
            setTimeout(() => {
              if (ButtonPressed === "Discard") {
                this.isCalculationsSaveEnabled = false;
                this.isCalendarSaveEnabled = false;
                this.isSaveEnabled = false;
                resolve(true);
              } else {
                resolve(false);
              }
            }, 50);
          }
        );
      } else {
        // Fallback if Helpers is not available
        const shouldDiscard = confirm('You have unsaved changes. Are you sure you want to discard them?');
        if (shouldDiscard) {
          this.isCalculationsSaveEnabled = false;
          this.isCalendarSaveEnabled = false;
          this.isSaveEnabled = false;
        }
        resolve(shouldDiscard);
      }
    });
  }
}
